import { TranslationService } from '@core/translation/translation.service';
import { QuoteDocumentType } from '@shared/invoice-types';
import { QuoteLifecycleStatus } from '@shared/models/quote';
import { BcmFinancialRecord } from '@shared/models/bcm-financial-record';

export function hasFinancialRecordInvoice(record: {
    invoice?: { id?: number | null } | null;
}): boolean {
    return !!record.invoice?.id;
}

export function hasFinancialRecordReceipt(record: {
    receipt?: { id?: number | null } | null;
}): boolean {
    return !!record.receipt?.id;
}

export function hasFinancialRecordQuote(record: {
    quote?: { id?: number | null } | null;
    type?: string | null;
}): boolean {
    return !!record.quote?.id || record.type === QuoteDocumentType.Quote;
}

export function canConvertFinancialRecordQuoteToOrder(record: {
    quote?: { id?: number | null; bcmOrdersId?: number | null; lifecycleStatus?: QuoteLifecycleStatus | string | null } | null;
    bcmQuotesId?: number | null;
    type?: string | null;
    status?: QuoteLifecycleStatus | string | null;
}): boolean {
    if (!hasFinancialRecordQuote(record)) {
        return false;
    }

    const quoteId = record.quote?.id ?? record.bcmQuotesId;

    if (!quoteId) {
        return false;
    }

    if (record.quote?.bcmOrdersId) {
        return false;
    }

    const lifecycleStatus = (record.quote?.lifecycleStatus ?? record.status) as QuoteLifecycleStatus | null;

    return lifecycleStatus === QuoteLifecycleStatus.Finalized
        || lifecycleStatus === QuoteLifecycleStatus.Sent
        || lifecycleStatus === QuoteLifecycleStatus.Accepted;
}

export function resolveFinancialRecordQuoteId(record: {
    quote?: { id?: number | null } | null;
    bcmQuotesId?: number | null;
}): number | null {
    const quoteId = record.quote?.id ?? record.bcmQuotesId;

    return quoteId != null ? Number(quoteId) : null;
}

export function resolveFinancialRecordNavigationPath(record: BcmFinancialRecord): string[] | null {
    if (hasFinancialRecordQuote(record)) {
        const quoteId = record.quote?.id ?? record.bcmQuotesId;

        if (!quoteId) {
            return null;
        }

        const lifecycleStatus = record.quote?.lifecycleStatus ?? record.status;

        if (lifecycleStatus === QuoteLifecycleStatus.Draft) {
            return ['accounting', 'quotes', String(quoteId)];
        }

        return ['accounting', 'quotes', 'view', String(quoteId)];
    }

    return ['accounting', 'invoices', 'view', record.number];
}

export function getFinancialRecordTypeLabel(type: string | null | undefined): string {
    if (!type) {
        return '—';
    }

    if (type.includes('.')) {
        return TranslationService.translate(type);
    }

    return TranslationService.translate(`accounting.financialDocuments.${type}`);
}
